import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiRequest } from 'helpers/api';
import { logException } from 'helpers/loggers';

import type { AutoCampaignPreset, CreateAutoCampaignPresetRequest } from './types';

// Hermosa's admin proxy already roots at /admin/v1, so the path it forwards must not repeat it.
export const AUTO_CAMPAIGN_PRESETS_PATH = 'proxy/marketing-integration/admin/auto-campaign-presets';

export const AUTO_CAMPAIGN_PRESETS_QUERY_KEY = 'autoCampaignPresets';

/** The status rides along so the form can tell a 403 from a name the API already has. */
export type AutoCampaignPresetError = Error & { status?: number };

type ValidationDetail = { loc?: (string | number)[]; msg?: string };

function presetError(message: string, status: number | undefined): AutoCampaignPresetError {
  return Object.assign(new Error(message), { status });
}

/** FastAPI returns a string `detail` for business errors and an array of them for 422s. */
export function formatPresetErrorDetail(detail: unknown): string | undefined {
  if (typeof detail === 'string') return detail.trim() || undefined;

  if (Array.isArray(detail)) {
    const messages = (detail as ValidationDetail[])
      .map(item => item.msg)
      .filter((msg): msg is string => !!msg);
    return messages.length ? messages.join('. ') : undefined;
  }

  return undefined;
}

/**
 * POST proxy/marketing-integration/admin/auto-campaign-presets
 * Reaches MIS as /admin/v1/auto-campaign-presets.
 * A seasonal preset (one with a start date) fans out a SyncAutomationsWorkflow per active
 * restaurant, so a duplicate submit is not harmless.
 */
export const useCreateAutoCampaignPreset = () => {
  const queryClient = useQueryClient();

  return useMutation<AutoCampaignPreset, AutoCampaignPresetError, CreateAutoCampaignPresetRequest>({
    mutationFn: async body => {
      let status: number | undefined;
      let errorPayload: unknown;

      const { data, error } = await apiRequest<AutoCampaignPreset>({
        path: AUTO_CAMPAIGN_PRESETS_PATH,
        method: 'POST',
        params: body,
        // A duplicate name or a rejected field is the admin's to fix, not a bug to page on.
        trackErrorsInSentry: (response: Response) => response.status >= 500,
        onError: (response: Response | undefined, _error: unknown, payload?: unknown) => {
          status = response?.status;
          errorPayload = payload;
        },
      });

      if (error) {
        const detail = formatPresetErrorDetail((errorPayload as { detail?: unknown })?.detail);
        const fallback = error instanceof Error ? error.message : '';
        throw presetError(detail ?? fallback, status);
      }

      if (!data) throw presetError('No auto campaign preset returned', status);
      return data;
    },

    onError: error => {
      logException({
        message: 'Failed to create auto campaign preset',
        error,
        context: null,
        tags: null,
      });
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [AUTO_CAMPAIGN_PRESETS_QUERY_KEY] });
    },
  });
};
